<template>
	<div class="similar-category mid-container" v-focus-section v-if="item?.type === 'node--movie'">
		<h2 class="vl-font-size--26">{{ $t("innerpage.more_like_that") }}</h2>
		<div class="similar-category--list">
			<TeaserMedium v-for="movie in similarItems" :key="movie.drupal_internal__nid" :item="movie" />
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from "vue"
	import TeaserMedium from "../components/teasers/TeaserMedium.vue"
	import { useMovieStore } from "../stores/movies"
	import { useSeriesStore } from "../stores/series"
	import { Movie } from "../types/Movie"
	import { Series } from "../types/Series"
	import axios from "axios"
	import { url } from "../helper"

	import api from "../composables/useApi"
	import { useI18n } from "vue-i18n"

	const movieStore = useMovieStore()
	const seriesStore = useSeriesStore()
	const { locale } = useI18n()

	const props = defineProps<{
		type: string
		item: Movie | Series
		id: number
	}>()

	const fetchedItems = ref<any[]>([])
	const loading = ref(false)

	// Get items from store (searching through {category, data} structure)
	const itemsFromStore = computed(() => {
		if (!props.item || !props.item.field_category) return []

		const currentCategories = Array.isArray(props.item.field_category)
			? props.item.field_category
			: [props.item.field_category]

		// Extract category IDs
		const categoryIds = currentCategories.map((cat: any) => cat.meta?.drupal_internal__target_id || cat).filter(Boolean)

		if (categoryIds.length === 0) return []

		const results: any[] = []

		// Search through movie store
		for (const categoryGroup of movieStore.state.movies as any[]) {
			if (categoryGroup.data && Array.isArray(categoryGroup.data)) {
				// Check if this category group matches any of our categories
				if (categoryIds.includes(categoryGroup.category)) {
					// Add all items except the current one
					const items = categoryGroup.data.filter((m: any) => m.drupal_internal__nid !== props.id)
					results.push(...items)
				}
			}
		}

		// Search through series store
		for (const categoryGroup of seriesStore.state.series as any[]) {
			if (categoryGroup.data && Array.isArray(categoryGroup.data)) {
				if (categoryIds.includes(categoryGroup.category)) {
					const items = categoryGroup.data.filter((s: any) => s.drupal_internal__nid !== props.id)
					results.push(...items)
				}
			}
		}

		return results
	})

	// Combined items: prefer store, fallback to fetched
	const similarItems = computed(() => {
		const storeItems = itemsFromStore.value
		if (storeItems.length > 0) return storeItems.slice(0, 9)
		return fetchedItems.value.slice(0, 9)
	})

	// Fetch items from API if not in store
	const fetchSimilarItems = async () => {
		if (!props.item || !props.item.field_category || itemsFromStore.value.length > 0 || loading.value) {
			return
		}

		loading.value = true
		try {
			const currentCategories = Array.isArray(props.item.field_category)
				? props.item.field_category
				: [props.item.field_category]

			const categoryIds = currentCategories
				.map((cat: any) => cat.meta?.drupal_internal__target_id || cat)
				.filter(Boolean)

			if (categoryIds.length === 0) return

			const requests = []

			// Fetch movies with matching categories
			requests.push(
				axios.get(`${url}/${locale.value}/jsonapi/node/movie?jsonapi_include=1&include=field_image`, {
					params: {
						"filter[field_category.meta.drupal_internal__target_id]": categoryIds.join(","),
						"filter[langcode]": locale.value,
						"page[limit]": 12,
						"filter[status]": 1,
						sort: "-changed",
					},
				})
			)

			// Fetch series with matching categories
			requests.push(
				axios.get(`${url}/${locale.value}/jsonapi/node/series?jsonapi_include=1&include=field_image`, {
					params: {
						"filter[field_category.meta.drupal_internal__target_id]": categoryIds.join(","),
						"filter[langcode]": locale.value,
						"filter[status]": 1,
						"page[limit]": 12,
					},
				})
			)

			const responses = await Promise.all(requests)

			// Combine and filter out the current item
			const combined = responses.flatMap((response) => response.data.data || [])
			fetchedItems.value = combined.filter((item: any) => item.drupal_internal__nid !== props.id)
		} catch (error) {
			console.error("Failed to fetch similar items:", error)
			fetchedItems.value = []
		} finally {
			loading.value = false
		}
	}

	// Watch for item changes and fetch if needed
	watch(
		() => props.item,
		async (newItem) => {
			if (newItem && itemsFromStore.value.length === 0) {
				await fetchSimilarItems()
			}
		},
		{ immediate: true }
	)
</script>

<style lang="scss" scoped>
	.similar-category {
		margin-top: 45px;
		margin-bottom: 150px;

		@include br.respond-to(br.$tablet-large) {
			margin-top: 30px;
			margin-bottom: 0;
		}

		h2 {
			margin-bottom: 35px;
		}
		&--list {
			display: grid;
			// grid-template-columns: repeat(auto-fill, minmax(100px, 265px));
			grid-template-columns: repeat(3, 1fr);
			justify-content: center;
			column-gap: 20px;
			row-gap: 50px;

			@include br.respond-to(800px) {
				grid-template-columns: repeat(auto-fill, minmax(100px, 265px));
				row-gap: 30px;
				column-gap: 15px;
			}

			@include br.respond-to(br.$mobile-large) {
				grid-template-columns: repeat(2, 1fr);
				row-gap: 10px;
				column-gap: 10px;
			}
			@include br.respond-to(360px) {
				grid-template-columns: repeat(auto-fill, minmax(100%, 150px));
			}
		}
	}
</style>
